/**
 * String utility functions for text manipulation, formatting, and validation
 */

import * as Api from '@ViewModels';

/**
 * Truncates text to a specified length with optional max line count
 * @source Original location: UiUtils.ts.backup line 890
 */

export const truncateTextToLength = (text: string, length: number, maxLineCount = 1000) => {
	let truncatedText = text || '';
	const lines = truncatedText
		.split('\n')
		.filter(x => !!x)
		.slice(0, maxLineCount);
	truncatedText = lines.join('\n');
	const len = Math.max(0, length);
	if (truncatedText.length > len) {
		truncatedText = truncatedText.substring(0, len);
		const lastWhiteSpaceIndex = truncatedText.search(/[\s ]$/);
		if (lastWhiteSpaceIndex >= 0) {
			truncatedText = truncatedText.substring(0, lastWhiteSpaceIndex);
		}
	}
	return truncatedText.trim();
};

/**
 * Converts HTML string to plain text
 * @source Original location: UiUtils.ts.backup line 908
 */
export const convertHtmlStringValueToPlainText = (htmlStringValue: string) => {
	const el = document.createElement('div');
	el.innerHTML = htmlStringValue;
	const text = el.textContent || '';
	if (el.parentNode) {
		el.parentNode.removeChild(el);
	}
	return text;
};

const LineBreakingTagNames = new Set([
	'ADDRESS',
	'BLOCKQUOTE',
	'CAPTION',
	'DD',
	'DIV',
	'DL',
	'DT',
	'H1',
	'H2',
	'H3',
	'H4',
	'H5',
	'H6',
	'LI',
	'OL',
	'P',
	'PRE',
	'TABLE',
	'TBODY',
	'TD',
	'TFOOT',
	'TH',
	'THEAD',
	'TR',
	'UL',
]);

const NonRenderedTagNames = new Set(['AUDIO', 'IFRAME', 'NOSCRIPT', 'OBJECT', 'SCRIPT', 'STYLE', 'TEXTAREA', 'VIDEO']);

/**
 * Converts HTML to plain text while keeping the line structure, unlike
 * {@link convertHtmlStringValueToPlainText}, which reads `textContent` and so renders
 * `<div>a</div><div>b</div>` as `ab`. Use this where the line breaks are part of the message —
 * a text message keeps them, and joining two lines silently changes what the recipient reads.
 */
export const convertHtmlStringValueToPlainTextLines = (htmlStringValue: string) => {
	const doc = new DOMParser().parseFromString(htmlStringValue || '', 'text/html');
	let text = '';

	const appendLineBreak = () => {
		if (text && !text.endsWith('\n')) {
			text += '\n';
		}
	};

	const walk = (node: Node) => {
		if (node.nodeType === Node.TEXT_NODE) {
			text += node.nodeValue ?? '';
			return;
		}
		if (node.nodeType !== Node.ELEMENT_NODE) {
			return;
		}
		const tagName = (node as HTMLElement).tagName;
		if (NonRenderedTagNames.has(tagName)) {
			return;
		}
		if (tagName === 'BR') {
			text += '\n';
			return;
		}
		const breaksLine = LineBreakingTagNames.has(tagName);
		if (breaksLine) {
			appendLineBreak();
		}
		node.childNodes.forEach(walk);
		if (breaksLine) {
			appendLineBreak();
		}
	};

	doc.body.childNodes.forEach(walk);

	return text.replace(/\n{3,}/g, '\n\n').replace(/^\n+|\n+$/g, '');
};

/**
 * Compares two strings for equality (case-insensitive)
 *  * @source Original location: UiUtils.ts.backup line 943
 */

export const stringsEqual = (
	firstString?: string,
	otherString?: string,
	options?: { caseInsensitiveCompare?: boolean }
) => {
	if (!!firstString && !!otherString) {
		if (!!options && !!options.caseInsensitiveCompare) {
			const lowerThis = firstString.toLocaleLowerCase();
			const lowerOtherString = otherString.toLocaleLowerCase();
			return lowerThis === lowerOtherString;
		}
	}

	if (firstString === otherString) {
		return true;
	}

	return false;
};

/**
 * Shortens a string to a maximum length with a separator
 * @source Original location: UiUtils.ts.backup line 976
 */
export const getShortenedString = (str: string, maxLen: number, separator = ' ') => {
	if (str?.length <= maxLen && !!str) {
		return str;
	}
	return str?.substring(0, str.lastIndexOf(separator, maxLen));
};

/**
 * Gets the last component of a path
 * @source Original location: UiUtils.ts.backup line 1004
 */
export const getLastPathComponent = (path: string) => {
	const pathComps = (path ? path : '').split('/').filter(x => !!x);
	const lastPathComp = pathComps.length > 0 ? pathComps[pathComps.length - 1] : '';
	return lastPathComp;
};

/**
 * Replaces the last component of a path
 * @source Original location: UiUtils.ts.backup line 1010
 */
export const replaceLastPathComponent = (path: string, replacement?: string) => {
	const pathComps = (path || '').split('/').filter(x => !!x);
	pathComps.splice(pathComps.length - 1, 1, replacement ? replacement : undefined);
	const result = pathComps.join('/');
	return result.charAt(0) === '/' ? result : `/${result}`;
};

/**
 * Gets the industry name by removing underscores
 * @source Original location: UiUtils.ts.backup line 1240
 */
export const getIndustryName = (industry: Api.Industry | 'All') => {
	return industry.replace(/_/gi, '');
};

/**
 * Gets the discount name from account discount percent
 * @source Original location: UiUtils.ts.backup line 963
 */
export const getDiscountName = (discount: Api.AccountDiscountPercent) => {
	switch (discount) {
		case Api.AccountDiscountPercent.Ten:
			return '10%';
		case Api.AccountDiscountPercent.Fifteen:
			return '15%';
		case Api.AccountDiscountPercent.Twenty:
			return '20%';
		case Api.AccountDiscountPercent.TwentyFive:
			return '25%';
		case Api.AccountDiscountPercent.Thirty:
			return '30%';
		case Api.AccountDiscountPercent.ThirtyFive:
			return '35%';
		case Api.AccountDiscountPercent.Forty:
			return '40%';
		case Api.AccountDiscountPercent.Fifty:
			return '50%';
		default:
			return '';
	}
};
